import type { GameplayTunables } from '../../shared/types/tunables';
import { DEFAULT_GAMEPLAY_TUNABLES } from './tunables';

/** One-level-deep partial: overrides target whole groups or individual values. */
export type GameplayTunablesOverride = {
  [K in keyof GameplayTunables]?: GameplayTunables[K] extends object
    ? Partial<GameplayTunables[K]>
    : GameplayTunables[K];
};

/**
 * The only supported way to read tunables. Nothing else may hard-code these values, so a
 * playtest change is a one-file change (D-010).
 */
export interface TunablesProvider {
  get(): GameplayTunables;
}

export function mergeTunables(
  base: GameplayTunables,
  override: GameplayTunablesOverride = {},
): GameplayTunables {
  return {
    shards: { ...base.shards, ...override.shards },
    teamBonus: { ...base.teamBonus, ...override.teamBonus },
    timersMs: { ...base.timersMs, ...override.timersMs },
    participationGraceMs: { ...base.participationGraceMs, ...override.participationGraceMs },
    bothRequiredMs: { ...base.bothRequiredMs, ...override.bothRequiredMs },
    portalDwellMs: override.portalDwellMs ?? base.portalDwellMs,
    countActionSettleMs: override.countActionSettleMs ?? base.countActionSettleMs,
    detector: { ...base.detector, ...override.detector },
    pose: { ...base.pose, ...override.pose },
  };
}

export function createTunablesProvider(
  override: GameplayTunablesOverride = {},
  base: GameplayTunables = DEFAULT_GAMEPLAY_TUNABLES,
): TunablesProvider {
  const resolved = mergeTunables(base, override);
  return { get: () => resolved };
}
